import { Injectable, inject, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, map, catchError, switchMap, tap, from } from 'rxjs';
import { ENV } from '@bodyanalytics/app-config';
// Import directly from the source modules rather than the '@bodyanalytics/ui-core'
// barrel. This file is itself re-exported by that barrel (src/index.ts), so importing
// from the barrel creates a circular import-alias (TS2303/TS2459) that fails Jest
// compilation for every consumer of ui-core (e.g. all front/admin libs).
import { ApiLocalRoutesEnum } from '../../enums/api-routes.enum';
import { AdminAuthStorageKeys } from '../../enums/admin-auth-storage-keys.enum';
import { SecureStorageService } from '../encryption/secure-storage.service';
import { SecureAdminTenantInfo } from '../../interfaces/secure-admin-tenant-info.interface';
import { AdminUserInfo } from '@bodyanalytics/data-models-ui';
import { safeErrorSummary } from '../../helper/safe-error-summary';

/**
 * Outcome of the post-login identity check (#2070).
 *
 *  - `'ok'`        — the caller is a known admin and the tenant info was stored.
 *  - `'not-admin'` — a REAL access decision: the backend answered (successfully)
 *                    that no admin record exists for the caller's verified
 *                    identity (200 + null body).
 *  - `'error'`     — we could NOT determine the answer: the identity lookup
 *                    (`GET tenant-api/admin-me`) failed with 401/5xx/network
 *                    (including a failed silent token fetch inside the Auth0
 *                    HTTP interceptor), or secure storage failed. Callers must
 *                    surface this as a retryable error, never as a denial —
 *                    collapsing it into `false` is exactly what produced the
 *                    silent bounce-to-login loop during the 2026-07-09 outage.
 */
export type AdminIdentityCheckStatus = 'ok' | 'not-admin' | 'error';

@Injectable({
  providedIn: 'root',
})
export class AdminAuthService {
  private http = inject(HttpClient);
  private env = inject(ENV);
  private secureStorage = inject(SecureStorageService);

  /**
   * Set the moment {@link resolveAdminIdentity} completes a real,
   * server-verified check ('ok' or 'not-admin') during THIS app load. Because
   * this service is `providedIn: 'root'`, one instance lives for the whole
   * browser-tab lifetime — a full page reload / new tab always constructs a
   * new instance, so this flag is a free, no-plumbing-required proxy for
   * "has session start already re-validated?" without needing any consumer
   * (guard, dashboard component) to change. See {@link hasValidAdminTenantInfo}.
   */
  private sessionRevalidated = false;

  /**
   * Dev-only logging. Suppressed in production builds so admin email lists,
   * decoded JWT claims, tenant info, and raw localStorage tokens are never
   * written to the browser console of a deployed app.
   */
  private debug(...args: unknown[]): void {
    if (isDevMode()) {
      console.warn(...args);
    }
  }

  /**
   * Checks if the current user is an admin and automatically stores tenant info.
   *
   * Boolean convenience wrapper over {@link resolveAdminIdentity} — kept for
   * callers that only need "did we end up with a stored admin identity?" and
   * must never receive an error (dashboard bootstrap, account details). It
   * cannot distinguish "not an admin" from "the lookup failed"; access-decision
   * call sites (AdminRoleGuard) must use {@link resolveAdminIdentity} instead.
   */
  checkAndStoreAdminTenantInfo(): Observable<boolean> {
    return this.resolveAdminIdentity().pipe(map(status => status === 'ok'));
  }

  /**
   * Resolves the post-login admin identity and stores the tenant info on success.
   *
   * Unlike the boolean wrapper, this keeps transport/storage FAILURES ('error')
   * distinct from a genuine access decision ('not-admin') — see
   * {@link AdminIdentityCheckStatus} and #2070. Never errors; failures are
   * folded into the 'error' status.
   */
  resolveAdminIdentity(): Observable<AdminIdentityCheckStatus> {
    this.debug('AdminAuthService: Starting resolveAdminIdentity');

    // DB-driven access: identity comes from the verified JWT SERVER-side
    // (admin-me), authorization from the users table. There is no client-side
    // email allowlist any more — the build-time ADMIN_EMAILS list locked every
    // newly provisioned tenant owner out until a frontend rebuild/redeploy,
    // and it added no security (it shipped readable in a public bundle; real
    // enforcement is server-side on every API call).
    return this.getAdminUserDetails().pipe(
      switchMap(userInfo => {
        this.debug('AdminAuthService: Received user info from backend:', userInfo ? '[present]' : '[none]');

        if (userInfo) {
          // Store consolidated tenant information encrypted in localStorage
          const adminTenantInfo: SecureAdminTenantInfo = {
            tenantId: userInfo.tenantId,
            userId: userInfo.id,
            userEmail: userInfo.emailAddress,
            userRole: userInfo.role,
            storedAt: Date.now(),
          };

          // The TenantInterceptor attaches the X-Tenant-ID header by reading
          // the PLAIN 'tenantId' localStorage key — it runs synchronously on
          // every request and cannot await the encrypted store. The sensitive
          // fields (userId/email/role) stay encrypted under ADMIN_TENANT;
          // tenantId is not a secret (it is sent as a request header anyway),
          // so mirror just it into the plain key the interceptor reads. Without
          // this, no admin request carries X-Tenant-ID and the API's
          // TenantContextGuard 403s every tenant-scoped call (e.g. the events
          // list fails with "Unable to load testing events"). The matching
          // removeItem('tenantId') in clearAdminTenantInfo() keeps logout symmetric.
          if (userInfo.tenantId) {
            localStorage.setItem('tenantId', userInfo.tenantId);
          }

          this.debug('AdminAuthService: Attempting to store admin tenant info (fields):', Object.keys(adminTenantInfo));

          return from(this.secureStorage.setSecureItem(AdminAuthStorageKeys.ADMIN_TENANT, adminTenantInfo)).pipe(
            tap(() => {
              this.debug('AdminAuthService: Successfully stored admin tenant info');
              // #2497: a fresh, server-verified check just completed this app
              // load — hasValidAdminTenantInfo() may now trust its short-TTL
              // cache instead of forcing another round trip on every call.
              this.sessionRevalidated = true;
            }),
            map((): AdminIdentityCheckStatus => 'ok'),
          );
        }
        // The backend answered successfully with no admin record for the
        // caller's verified identity — a real access decision, not a failure.
        // #2497: this is exactly the demotion/off-boarding case — clear any
        // stale cached tenant info so it cannot outlive the decision that just
        // invalidated it.
        this.debug('AdminAuthService: No user info received from backend');
        this.clearAdminTenantInfo();
        this.sessionRevalidated = true;
        return of<AdminIdentityCheckStatus>('not-admin');
      }),
      // HTTP failure from the identity lookup (401/5xx/network — including a
      // failed silent token fetch inside the Auth0 interceptor), or a secure
      // storage failure: we could not DETERMINE the answer (#2070).
      catchError(error => {
        console.error('AdminAuthService: identity lookup/storage failed:', safeErrorSummary(error));
        return of<AdminIdentityCheckStatus>('error');
      }),
    );
  }

  /**
   * Fetches the CALLER's admin user record from the backend.
   *
   * `GET tenant-api/admin-me` derives the caller's email from the verified JWT
   * server-side — no email rides in the URL (PII in request paths/logs) and a
   * caller can never look up anyone else's record. The Auth0 HTTP interceptor
   * attaches the access token (`tenant-api/*` is token-required in the admin
   * allowed-list); a failed silent token fetch surfaces as a request error,
   * which `resolveAdminIdentity` classifies as 'error'.
   *
   * The endpoint returns 200 with a `null` body when no admin record exists
   * for the caller (Prisma `findUnique` miss), so a null NEXT emission means
   * "not an admin". HTTP failures (5xx/network) are deliberately NOT swallowed
   * into `null` here — collapsing them made a dead backend indistinguishable
   * from a revoked account (#2070). They propagate to `resolveAdminIdentity`,
   * which classifies them as `'error'`.
   */
  private getAdminUserDetails(): Observable<AdminUserInfo | null> {
    const url = `${this.env.api}/${ApiLocalRoutesEnum.API_SUFFIX_TENANT}/admin-me`;

    return this.http.get<AdminUserInfo | null>(url);
  }

  /**
   * Gets the complete admin tenant info
   */
  async getAdminTenantInfo(): Promise<SecureAdminTenantInfo | null> {
    try {
      return await this.secureStorage.getSecureItem<SecureAdminTenantInfo>(AdminAuthStorageKeys.ADMIN_TENANT);
    } catch (error) {
      console.error('AdminAuthService: Error retrieving admin tenant info:', safeErrorSummary(error));
      return null;
    }
  }

  /**
   * Gets the tenant ID for the current admin
   */
  async getTenantId(): Promise<string | null> {
    const info = await this.getAdminTenantInfo();
    return info?.tenantId || null;
  }

  /**
   * Gets the user ID for the current admin
   */
  async getUserId(): Promise<string | null> {
    const info = await this.getAdminTenantInfo();
    return info?.userId || null;
  }

  /**
   * Gets the user email for the current admin
   */
  async getUserEmail(): Promise<string | null> {
    const info = await this.getAdminTenantInfo();
    return info?.userEmail || null;
  }

  /**
   * Gets the user role for the current admin
   */
  async getUserRole(): Promise<string | null> {
    const info = await this.getAdminTenantInfo();
    return info?.userRole || null;
  }

  /**
   * Checks whether the cached admin/tenant identity is still trustworthy
   * enough to skip a server round trip (#2497).
   *
   * ## Exposure this guards against
   * A demoted/off-boarded admin's `SecureAdminTenantInfo` (role, tenantId,
   * userId) stays in encrypted localStorage until something re-validates or
   * clears it. This method decides how long that stale entry may be trusted
   * client-side before a caller is forced back to {@link resolveAdminIdentity}.
   * The 2026-07-29 unknowns-register finding (F73) was that the old 24-hour
   * default let that window stretch to nearly a full day.
   *
   * ## What a demoted admin can still see/do client-side within the window
   * (i.e. what THIS cache controls) — purely cosmetic/navigational, no data:
   *  - `AdminRoleGuard.resolveAccess()` now calls this method: a cached role
   *    is only trusted while `hasValidAdminTenantInfo()` is `true`, so
   *    guard-gated route access is itself bounded to the same 10-minute /
   *    session-start window as everything else here — it is NOT an unbounded
   *    exposure for the life of the tab. Once the window lapses (or on a
   *    fresh app load), the guard forces a real `resolveAdminIdentity()`
   *    check before letting a cached role through.
   *  - Within that bounded window: the admin app shell keeps rendering as if
   *    the role were still valid — sidebar/nav items gated on the cached
   *    `userRole` stay visible without a backend round trip for each one.
   *  - Whatever was already fetched into component/store state before the
   *    demotion (e.g. a dashboard already loaded) stays visible in memory —
   *    ordinary SPA behavior, not extended by this cache.
   *  - `getTenantId()/getUserId()/getUserEmail()/getUserRole()` keep resolving
   *    from the stale cache for the remainder of the window, so any purely
   *    client-side conditional keyed on them (which menu entries render, etc.)
   *    keeps acting "as admin" until the next TTL-triggered revalidation.
   *
   * ## What FAILS regardless (server-side, unaffected by this cache)
   *  - Every actual API call. Each backend endpoint independently re-checks
   *    `x-tenant-id` membership and the caller's role on every request, so
   *    list/detail/create/update/delete calls 401/403 the moment the demotion
   *    is reflected server-side. No real data is read or written client-side
   *    access alone cannot reach it.
   *
   * ## Revocation story chosen
   *  - **TTL: 10 minutes** (down from 24h; the issue's suggested 5-15 min
   *    range). Short enough that the residual "ghost admin shell" window is a
   *    minor UX/cosmetic issue, not a real access exposure (data access is
   *    already server-enforced); long enough that normal admin navigation
   *    inside a session doesn't pay a lookup round trip on every route.
   *  - **Re-validated on every fresh app load ("session start"), not just by
   *    TTL.** {@link sessionRevalidated} starts `false` for every new instance
   *    of this `providedIn: 'root'` service — which happens exactly once per
   *    full page load/new tab, since Angular tears down and reconstructs the
   *    whole injector on reload. Until {@link resolveAdminIdentity} completes
   *    at least one real, server-verified check THIS app load, this method
   *    always returns `false` — a day-old (or even a minute-old, from a prior
   *    tab/reload) localStorage entry is never trusted on faith. Once that
   *    check succeeds, subsequent calls trust the cache for the 10-minute TTL
   *    without another round trip, preserving the cache's original purpose.
   *  - **No event-driven invalidation.** Considered per the issue, but there is
   *    no push channel (WebSocket/SSE/BroadcastChannel) anywhere in the
   *    frontend today that could carry a "your role changed" signal — building
   *    one is out of scope for this fix (issue #2497 explicitly excludes
   *    "real-time push-based revocation infrastructure" as out of scope). A
   *    short, session-start-revalidated TTL is the pragmatic interim story;
   *    revisit if/when a push channel exists for another reason.
   *  - `resolveAdminIdentity()` also now clears any stale cached tenant info
   *    the moment it determines `'not-admin'` (demoted/off-boarded), instead
   *    of leaving the old entry to expire on its own — see the two
   *    `clearAdminTenantInfo()` call sites above.
   */
  async hasValidAdminTenantInfo(maxAgeMs: number = 10 * 60 * 1000): Promise<boolean> {
    if (!this.sessionRevalidated) return false;

    const info = await this.getAdminTenantInfo();
    if (!info) return false;

    const age = Date.now() - info.storedAt;
    return age < maxAgeMs;
  }

  /**
   * Debug method to check what's stored in localStorage (for development only)
   */
  async debugStoredInfo(): Promise<void> {
    this.debug('=== DEBUG: Admin Tenant Info ===');
    const info = await this.getAdminTenantInfo();
    this.debug('Stored admin tenant info:', info ? '[present]' : null);
    this.debug('Raw localStorage item exists:', !!localStorage.getItem(AdminAuthStorageKeys.ADMIN_TENANT));
    this.debug('Session key exists:', !!sessionStorage.getItem(AdminAuthStorageKeys.SESSION_KEY));
    this.debug('================================');
  }

  /**
   * Clears stored admin tenant info (for logout)
   */
  clearAdminTenantInfo(): void {
    this.secureStorage.removeSecureItem(AdminAuthStorageKeys.ADMIN_TENANT);
    this.secureStorage.clearSessionKey();

    // Also remove any legacy localStorage items for backward compatibility
    localStorage.removeItem('tenantId');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');

    // #2497: nothing is left to trust — the next hasValidAdminTenantInfo()
    // check must not treat a stale in-memory flag as a fresh session-start
    // revalidation just because this same instance ran one earlier.
    this.sessionRevalidated = false;
  }
}
